import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import { Header } from "../../components/layout/Header";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Input } from "../../components/common/Input";
import { useAuth } from "../../contexts/AuthContext";
import { api } from "../../services/api";
import { EventType } from "../../types";
import { COLORS } from "../../config/colors";

const EVENT_TYPES = [
  {
    type: EventType.CASUAL_ROUND,
    label: "Casual Round",
    icon: "⛳",
    desc: "A regular round with friends",
  },
  {
    type: EventType.LEAGUE_GAME,
    label: "League Game",
    icon: "🏆",
    desc: "Part of a regular league",
  },
  {
    type: EventType.TOURNAMENT,
    label: "Tournament",
    icon: "🥇",
    desc: "Multi-round tournament",
  },
  {
    type: EventType.MATCH,
    label: "Match",
    icon: "⚔️",
    desc: "Head-to-head match play",
  },
  {
    type: EventType.OUTING,
    label: "Outing",
    icon: "🎉",
    desc: "Group outing or corporate event",
  },
  {
    type: EventType.TRIP,
    label: "Trip",
    icon: "✈️",
    desc: "Multi-day golf trip",
  },
  {
    type: EventType.PARIMUTUEL_BETTING,
    label: "Parimutuel Betting",
    icon: "🎲",
    desc: "Kentucky Derby-style parimutuel pool",
  },
];

export default function CreateEventPage() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const [step, setStep] = useState<"type" | "details">("type");
  const [selectedType, setSelectedType] = useState<EventType | null>(null);

  // Details form
  const [name, setName] = useState("");
  const [courseName, setCourseName] = useState("");
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedType || !user) return;
    setError("");
    setLoading(true);

    try {
      const eventDate = date
        ? new Date(`${date}T${time || "08:00"}:00`)
        : new Date();
      const isPari = selectedType === EventType.PARIMUTUEL_BETTING;

      const res = (await api.events.create({
        createdByName: user.displayName,
        eventType: selectedType,
        name: name || undefined,
        notes: notes || undefined,
        firstRound: {
          courseId: "manual",
          courseName: courseName || "TBD",
          teeBox: "White",
          date: eventDate.toISOString(),
          scheduledTime: time || undefined,
          gameConfig: {
            templateId: isPari ? "parimutuel" : "stroke-play",
            teamMode: "individual",
            scoringMethod: isPari ? "parimutuel" : "stroke",
          },
        },
      })) as { data?: { id: string } };

      const eventId = res.data?.id;
      if (!eventId) throw new Error("No event ID returned");

      if (isPari) {
        // Also create the parimutuel sub-document
        await api.pariMutuel.create({
          eventId,
          eventName: name || `Parimutuel – ${new Date().toLocaleDateString()}`,
        });
        navigate(`/events/${eventId}/parimutuel`);
      } else {
        navigate(`/events/${eventId}`);
      }
    } catch (err: any) {
      setError(err.message ?? "Failed to create event");
    } finally {
      setLoading(false);
    }
  };

  if (step === "type") {
    return (
      <>
        <Header title='Create Event' showBack />
        <div className='px-4 py-4 space-y-2'>
          <p className='text-sm text-gray-500 mb-3'>
            What type of event are you creating?
          </p>
          {EVENT_TYPES.map(({ type, label, icon, desc }) => (
            <Card
              key={type}
              onClick={() => {
                setSelectedType(type);
                setStep("details");
              }}
              accent={
                type === EventType.PARIMUTUEL_BETTING
                  ? COLORS.parimutuel
                  : COLORS.events
              }
            >
              <div className='flex items-center gap-3'>
                <span className='text-2xl'>{icon}</span>
                <div className='flex-1'>
                  <p className='font-semibold text-primary-800'>{label}</p>
                  <p className='text-xs text-gray-500'>{desc}</p>
                </div>
                <ChevronRight size={18} className='text-gray-300' />
              </div>
            </Card>
          ))}
        </div>
      </>
    );
  }

  const selectedInfo = EVENT_TYPES.find((t) => t.type === selectedType)!;

  return (
    <>
      <Header title='Event Details' showBack />
      <div className='px-4 py-4'>
        {/* Type badge */}
        <div className='flex items-center gap-2 mb-4 p-3 bg-white rounded-2xl border border-gray-100 shadow-sm'>
          <span className='text-xl'>{selectedInfo.icon}</span>
          <span className='font-semibold text-primary-800'>
            {selectedInfo.label}
          </span>
          <button
            onClick={() => setStep("type")}
            className='ml-auto text-xs text-secondary-400 font-medium hover:underline'
          >
            Change
          </button>
        </div>

        <form onSubmit={handleCreate} className='space-y-4'>
          <Input
            label='Event Name (optional)'
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={`e.g. Saturday Game`}
          />

          {selectedType !== EventType.PARIMUTUEL_BETTING && (
            <Input
              label='Course / Venue'
              value={courseName}
              onChange={(e) => setCourseName(e.target.value)}
              placeholder='e.g. Rolling Hills CC'
            />
          )}

          <div className='flex gap-2'>
            <div className='flex-1'>
              <Input
                label='Date'
                type='date'
                value={date}
                onChange={(e) => setDate(e.target.value)}
              />
            </div>
            <div className='flex-1'>
              <Input
                label='Time'
                type='time'
                value={time}
                onChange={(e) => setTime(e.target.value)}
              />
            </div>
          </div>

          <Input
            label='Notes (optional)'
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder='Any extra details...'
          />

          {error && <p className='text-sm text-red-500'>{error}</p>}

          <Button type='submit' fullWidth loading={loading}>
            Create Event
          </Button>
        </form>
      </div>
    </>
  );
}
